import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { WalteroButton, WalteroTypography } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';

const resolveSubscriptionLabel = (subscription, subscriptionTypes) => {
  if (!subscription) {
    return 'Unknown';
  }

  const baseName = subscription.customName || subscription.displayName;
  if (baseName) {
    return baseName;
  }

  const type = subscriptionTypes.find((typeItem) => typeItem.id === subscription.subscriptionTypeId);
  if (type) {
    return type.name;
  }

  return subscription.subscriptionTypeId || 'Unknown';
};

const resolveTypeLabel = (subscriptionTypeId, subscriptionTypes) => {
  const type = subscriptionTypes.find((typeItem) => typeItem.id === subscriptionTypeId);
  return type ? type.name : subscriptionTypeId || 'Unknown';
};

const EditSensorAssignmentModal = ({
  open,
  onClose,
  assignment,
  organizationSubscriptions = [],
  subscriptionTypes = [],
  onUpdated
}) => {
  const [selectedSubscriptionTypeId, setSelectedSubscriptionTypeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (assignment && open) {
      setSelectedSubscriptionTypeId(assignment.subscriptionTypeId || '');
      setError(null);
    }
  }, [assignment, open]);

  const availableSubscriptions = useMemo(
    () =>
      organizationSubscriptions.filter(
        (subscription) =>
          subscription.status !== 'inactive' &&
          subscription.subscriptionTypeId
      ),
    [organizationSubscriptions]
  );

  const currentSubscriptionLabel = useMemo(() => {
    if (!assignment) {
      return 'Unknown';
    }

    if (!assignment.subscriptionTypeId) {
      return 'No subscription assigned';
    }

    const fromOrganization = organizationSubscriptions.find(
      (subscription) => subscription.subscriptionTypeId === assignment.subscriptionTypeId
    );

    if (fromOrganization) {
      return resolveSubscriptionLabel(fromOrganization, subscriptionTypes);
    }

    return resolveTypeLabel(assignment.subscriptionTypeId, subscriptionTypes);
  }, [assignment, organizationSubscriptions, subscriptionTypes]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!assignment?.sensorId || !assignment?.organizationId) {
      setError('Missing sensor or organization identifier');
      return;
    }

    if (!selectedSubscriptionTypeId) {
      setError('Subscription type is required');
      return;
    }

    if (selectedSubscriptionTypeId === assignment.subscriptionTypeId && assignment.assignmentType === 'manual') {
      onClose?.();
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await subscriptionService.assignSensorSubscription(
        assignment.sensorId,
        assignment.organizationId,
        selectedSubscriptionTypeId
      );

      onUpdated?.();
      onClose?.();
    } catch (err) {
      console.error('Error updating sensor assignment:', err);
      setError(err.message || 'Failed to update sensor assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!assignment?.sensorId || !assignment?.organizationId) {
      setError('Missing sensor or organization identifier');
      return;
    }

    const confirmed = window.confirm(
      'Resetting will remove this manual assignment and return the sensor to the organization default. Continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await subscriptionService.resetSensorToDefault(assignment.sensorId, assignment.organizationId);
      onUpdated?.();
      onClose?.();
    } catch (err) {
      console.error('Error resetting sensor assignment to default:', err);
      setError(err.message || 'Failed to reset sensor assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  if (!assignment) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <WalteroTypography variant="h5" sx={{ color: 'waltero.primary.coreBlue', fontWeight: 600 }}>
            Edit Sensor Assignment
          </WalteroTypography>
          <WalteroTypography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Sensor: {assignment.sensorName || assignment.sensorId}
          </WalteroTypography>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 3, backgroundColor: 'grey.50', borderRadius: 1, p: 2 }}>
            <WalteroTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Current Assignment
            </WalteroTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <WalteroTypography variant="caption" color="text.secondary">
                  Subscription
                </WalteroTypography>
                <WalteroTypography variant="body2">
                  {currentSubscriptionLabel}
                </WalteroTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <WalteroTypography variant="caption" color="text.secondary">
                  Assignment Type
                </WalteroTypography>
                <WalteroTypography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {assignment.assignmentType || 'manual'}
                </WalteroTypography>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ minWidth: 280 }} disabled={loading}>
                <InputLabel id="edit-sensor-subscription-select">Subscription *</InputLabel>
                <Select
                  labelId="edit-sensor-subscription-select"
                  value={selectedSubscriptionTypeId}
                  label="Subscription *"
                  onChange={(event) => setSelectedSubscriptionTypeId(event.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        minWidth: 320
                      }
                    }
                  }}
                >
                  {availableSubscriptions.map((subscription) => (
                    <MenuItem key={subscription.id} value={subscription.subscriptionTypeId}>
                      {resolveSubscriptionLabel(subscription, subscriptionTypes)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <WalteroButton
            variant="outlined"
            color="primary"
          onClick={handleResetToDefault}
          disabled={loading || assignment?.assignmentType !== 'manual'}
        >
          RESET TO DEFAULT
        </WalteroButton>
          <WalteroButton
            onClick={handleDialogClose}
            variant="outlined"
            disabled={loading}
          >
            CANCEL
          </WalteroButton>
          <WalteroButton
            type="submit"
            variant="contained"
            disabled={loading || !selectedSubscriptionTypeId}
          >
            {loading ? 'SAVING...' : 'SAVE CHANGES'}
          </WalteroButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditSensorAssignmentModal;
